/**
 * @file itemCostUnit.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-21
 * @modified 2026-10-23
 * @description What an inventory item's cost is PER, and that cost for one
 *              unit of a line (Cam, 21 Oct 2026: "an order for 1T of 25kg
 *              bags (40) … generated a PO-D which pulled a cost from inventory
 *              and multiplied it by 40 when the cost price in the inventory is
 *              per tonne").
 *
 *   THE ITEM'S COST IS PER THE ITEM'S UNIT. inventory.items.purchase_unit_price
 *   is the cost of one of the item's own quantity units, and the item's unit is
 *   its unit_weight_kg: 1000 = a tonne, 25 = a 25kg bag, 500 = a tote. An item
 *   with no unit weight on a bulk sales code (201 / 202 / 204 / 206 / 209 —
 *   the same list as utils/tonnage.js) is priced per tonne. Milk powder (200P)
 *   keeps its own rule — its weekly cost is turned into a cost per bag where
 *   it is read — and is never converted here.
 *
 *   A LINE COUNTED IN ANOTHER UNIT takes the cost per ITS unit: cost per tonne
 *   × the line's unit kg ÷ 1000 — £464 a tonne is £11.60 a 25kg bag — and in
 *   general item cost × line kg ÷ the item's kg. The line's unit is its own
 *   unit weight, else the item's (utils/lineUnits.lineWeightKg); a line set to
 *   None (no weight) takes the cost as it is.
 *
 *   AN ITEM WITH A COST AND NO UNIT cannot be converted: nobody knows whether
 *   £69.375 is a bag or a tonne. The screens ask the person ordering what it is
 *   per (and offer to save the unit on the item for next time); until they
 *   answer, the cost is not filled in. The server does the same in SQL
 *   (utils/lineCostSql ITEM_COST_FACTOR_SQL): a line with a unit of its own
 *   against such an item gets no item cost (NULL — "-" on screen, £0 on a PO)
 *   rather than one multiplied by the wrong unit; a line with no unit of its
 *   own is in the item's unknown unit, so the cost stands as it is.
 *
 *   The money per kg stays the same whenever a line's unit changes between two
 *   known weights (scaleForUnitChange): the grids re-cost a line when its Units
 *   or Weight change, and the order → PO rebuild re-costs a PO line whose unit
 *   followed the order line's.
 *
 *   ITEMS IN TOTES ARE COSTED PER TONNE (Cam, 23 Oct 2026 — "the cost prices
 *   for inventory items in totes is per tonne, and when ordered, 1 tote should
 *   reflect half that cost price"). An item whose name or descriptions say
 *   tote / totes / tote bags (isToteItem — not an NWF delivery charge on 213,
 *   not "Tote EMPTY" or a shopping tote) is saved with unit weight 1000
 *   (scripts/totes-cost-per-tonne.js), so its cost reads per tonne by the rule
 *   above; one with no unit weight yet is per tonne too (costBasisOf 'tote'),
 *   so a new tote item needs no question. Picked on a line, such an item
 *   starts on its tote ("500kg tote", or the size its name gives —
 *   defaultLineWeightKg / toteSizeKg), so "1" is one tote at half the cost.
 *
 *   The server's utils/itemCostUnit.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { isNoWeight, lineWeightKg } from './lineUnits';
import { unitLabelFor } from './units';

/** Sales codes whose quantity is tonnes with no weight set (utils/tonnage.js). */
export const COST_BULK_SALES_CODES = ['201', '202', '204', '206', '209'];

/** Milk powder: its cost is per bag already, from the weekly cost table. */
export const MILK_POWDER_SALES_CODE = '200P';

/** A product in totes: these words in its name or descriptions. */
export const TOTE_WORDS_RE = /\btotes?\b/i;
/** …unless it is not a product in a tote: an empty tote, a shopping tote. */
export const TOTE_NOT_PRODUCT_RE = /\b(empty|shopping)\b/i;
/** NWF's per-tote delivery charges ("Ex Postcode (NWF Del) 1 Tote") are not products in totes. */
export const TOTE_NOT_PRODUCT_SALES_CODES = ['213'];
/** A tote holds this unless its name says otherwise. */
export const DEFAULT_TOTE_KG = 500;

const blank = (v) => v === '' || v === null || v === undefined;
const num = (v) => {
  if (blank(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const positive = (v) => {
  const n = num(v);
  return n !== null && n > 0 ? n : null;
};
const round4 = (n) => Math.round((Number(n) + Number.EPSILON) * 10000) / 10000;
const codeOf = (item) => String(item?.sales_account_code ?? item?.salesAccountCode ?? '').trim().toUpperCase();

/** The words an item (or a line) is described by, one string. */
export function itemWordsOf(item) {
  if (!item) return '';
  return [item.item_name, item.name, item.description, item.sales_description, item.purchase_description]
    .filter(w => w !== null && w !== undefined && String(w).trim() !== '').join(' ');
}

/** Do these words say the product is in totes (and is a product)? */
export function isToteWords(words) {
  const w = String(words || '');
  return TOTE_WORDS_RE.test(w) && !TOTE_NOT_PRODUCT_RE.test(w);
}

/** Is this inventory item a product in totes (Cam, 23 Oct 2026: costed per tonne)? */
export function isToteItem(item) {
  if (!item) return false;
  if (TOTE_NOT_PRODUCT_SALES_CODES.includes(codeOf(item))) return false;
  return isToteWords(itemWordsOf(item));
}

/**
 * What one tote holds, from the words: "Tote 500kg", "0.5T totes" / "1/2
 * totes" (500), "1t tote" (1000); a figure under 100 kg is an additive's
 * ("Deccox 2kg in totes"), not the tote's. Otherwise 500.
 */
export function toteSizeKg(words) {
  const w = String(words || '');
  const kgs = [...w.matchAll(/(\d+(?:\.\d+)?)\s*kg\b/gi)].map(m => Number(m[1])).filter(n => n >= 100 && n <= 1500);
  if (kgs.length) return kgs[0];
  if (/\b(0?\.5|1\/2)\s*(t|tonne|totes?)\b|\bhalf[\s-]*(t|tonne)\b/i.test(w)) return 500;
  if (/\b1\s*(t|tonne)\b/i.test(w)) return 1000;
  return DEFAULT_TOTE_KG;
}

/**
 * The unit a line of this item starts on, when it is not the item's own:
 * a tote item costed per tonne (unit weight 1000, or none) starts on its tote
 * ("500kg tote"), so "1" is typed for one tote. null = the item's unit.
 */
export function defaultLineWeightKg(item) {
  if (!isToteItem(item)) return null;
  const size = toteSizeKg(itemWordsOf(item));
  const own = positive(item.unit_weight_kg ?? item.unitWeightKg);
  if (own !== null && own !== 1000) return null;
  return size === (own ?? 1000) ? null : size;
}

/**
 * What the item's cost is per.
 * @returns {{ kg: number|null, how: 'item'|'bulk'|'milk_powder'|null }}
 *   kg null with how null = not known (ask); how 'milk_powder' = never converted.
 */
export function costBasisOf(item) {
  if (!item) return { kg: null, how: null };
  if (codeOf(item) === MILK_POWDER_SALES_CODE) return { kg: null, how: 'milk_powder' };
  const own = positive(item.unit_weight_kg ?? item.unitWeightKg);
  if (own !== null) return { kg: own, how: 'item' };
  // An item in totes is costed per tonne (Cam, 23 Oct 2026).
  if (isToteItem(item)) return { kg: 1000, how: 'tote' };
  if (COST_BULK_SALES_CODES.includes(codeOf(item))) return { kg: 1000, how: 'bulk' };
  return { kg: null, how: null };
}

/** "tonne", "25kg bag", "500kg tote", "12.5kg" — what one unit is called. */
export function perUnitWords(kg) {
  const k = positive(kg);
  if (k === null) return '';
  if (k === 1000) return 'tonne';
  return unitLabelFor(k);
}

/** £ with 2 to 4 places, for the notes these helpers write. */
export function costMoney(v) {
  const n = num(v);
  if (n === null) return '';
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

/**
 * A cost per `fromKg` as a cost per `toKg` (the money per kg kept). Either
 * weight unknown or None, or the two the same: the cost as it is.
 */
export function convertCost(cost, fromKg, toKg) {
  const c = num(cost);
  if (c === null) return null;
  const from = positive(fromKg);
  const to = positive(toKg);
  if (from === null || to === null || from === to) return c;
  return round4((c * to) / from);
}

/**
 * The item's cost for one unit of a line.
 *
 * @param {object} item            inventory row (unit_weight_kg, sales_account_code)
 * @param {object} opts
 * @param {number|string} opts.cost       the item's cost (purchase_unit_price)
 * @param {number|string} opts.lineWeight the line's OWN unit weight ('' = the item's, 0 = None)
 * @param {number|string} [opts.basisKg]  what the cost is per, when the person said so
 * @returns {{ cost: number|null, raw: number|null, basisKg: number|null, lineKg: number|null,
 *             converted: boolean, needsUnit: boolean, note: string }}
 *   needsUnit: the item has a cost and nothing says what it is per — ask; cost
 *   is then null (never filled in a wrong unit).
 */
export function itemCostForLine(item, { cost = item?.purchase_unit_price, lineWeight = '', basisKg = null } = {}) {
  const raw = num(cost);
  const out = { cost: raw, raw, basisKg: null, lineKg: null, converted: false, needsUnit: false, note: '' };
  if (raw === null || raw === 0) return out;
  const basis = costBasisOf(item);
  if (basis.how === 'milk_powder') return out;
  if (isNoWeight(lineWeight)) return out;
  const answered = positive(basisKg);
  const basisKgUsed = answered ?? basis.kg;
  if (basisKgUsed === null) {
    return { ...out, cost: null, needsUnit: true,
      note: `${costMoney(raw)} on the item — no unit on the item, so nobody knows what that cost is per` };
  }
  const lineKg = lineWeightKg(lineWeight, item?.unit_weight_kg ?? item?.unitWeightKg) ?? basisKgUsed;
  const value = convertCost(raw, basisKgUsed, lineKg);
  const converted = lineKg !== basisKgUsed;
  return {
    cost: value, raw, basisKg: basisKgUsed, lineKg, converted, needsUnit: false,
    note: converted
      ? `${costMoney(raw)} a ${perUnitWords(basisKgUsed)} on the item = ${costMoney(value)} a ${perUnitWords(lineKg)}`
      : ''
  };
}

/** Does this item need the person ordering to say what its cost is per? */
export function needsCostUnit(item, cost = item?.purchase_unit_price) {
  const c = num(cost);
  if (c === null || c === 0) return false;
  const basis = costBasisOf(item);
  return basis.how === null;
}

/**
 * A line's unit moved from `oldKg` to `newKg` (each the line's effective unit
 * weight — its own, else its item's): its cost per unit follows, so the cost
 * per tonne stays the same. Unknown or None either side, or no change: null
 * (leave the cost alone).
 */
export function scaleForUnitChange(cost, oldKg, newKg) {
  const c = num(cost);
  const from = positive(oldKg);
  const to = positive(newKg);
  if (c === null || from === null || to === null || from === to) return null;
  return round4((c * to) / from);
}

/**
 * The units a person can say an item's cost is per: a tonne and the fixed
 * pack sizes, plus the line's own unit when it is another (so "per 12.5kg"
 * can be answered too).
 */
export function costUnitChoices(lineKg = null) {
  const base = [
    { value: '1000', label: 'a tonne' },
    { value: '500', label: 'a 500kg tote' },
    { value: '25', label: 'a 25kg bag' },
    { value: '20', label: 'a 20kg bag' }
  ];
  const k = positive(lineKg);
  if (k !== null && !base.some(o => Number(o.value) === k)) {
    base.push({ value: String(k), label: `a ${perUnitWords(k)}` });
  }
  return base;
}
